import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users, Plus, X, ArrowRightLeft, RefreshCw, Download, Upload,
  History, Wallet, AlertTriangle, Undo2, Camera,
} from "lucide-react";
import { fmtKES } from "@/lib/format";

/**
 * /admin/team-corrections — post-auction squad + finance control panel.
 * Every mutation is server-side atomic (see /admin/teams/{id}/players/*).
 * The Admin can safely:
 *   • add a free-agent to a team (charges purse)
 *   • remove a player (refunds purse)
 *   • move a player between teams (dialog defaults to current price, editable)
 *   • reverse a completed auction sale
 *   • view wallet transactions + bid history
 *   • download and re-upload the players.xlsx to bulk-correct allocations
 */
export default function TeamCorrections() {
  const [snap, setSnap] = useState(null);
  const [freeAgents, setFreeAgents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // {kind, team, player?}

  const reload = useCallback(async () => {
    try {
      const [o, players] = await Promise.all([
        api.get("/admin/team-corrections/overview"),
        api.get("/players"),
      ]);
      setSnap(o.data);
      setFreeAgents((players.data || []).filter(p => !p.sold));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const removePlayer = async (team, player) => {
    if (!window.confirm(`Remove ${player.name} from ${team.team_name}? Team is refunded ${fmtKES(player.sold_amount)}.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/teams/${team.team_id}/players/remove`, { player_id: player.id });
      toast.success("Player removed · purse refunded");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const uploadAllocations = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/import/team-allocations", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [
        `Adds: ${data.adds.length}`, `Moves: ${data.moves.length}`,
        `Removes: ${data.removes.length}`, `Unchanged: ${data.unchanged}`,
        `Errors: ${data.errors.length}`,
      ];
      toast.success(`Import applied · ${parts.join(" · ")}`);
      if (data.errors.length) console.warn("Import errors", data.errors);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!snap) return <div className="p-8 text-zinc-500" data-testid="team-corrections-loading">Loading squad snapshot…</div>;

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="team-corrections">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Auction Management</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white leading-none">
            Team &amp; Player Corrections
          </h1>
          <div className="text-xs text-zinc-500 mt-2">
            Every mutation is atomic — purse deductions, refunds and roster changes stay in sync.
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" className="border-white/20 text-white" data-testid="btn-export-players">
            <a href={`${API_BASE}/exports/players.xlsx`} target="_blank" rel="noreferrer">
              <Download className="w-4 h-4 mr-2" /> Download Players
            </a>
          </Button>
          <Button asChild variant="outline" className="border-white/20 text-white" data-testid="btn-export-teams">
            <a href={`${API_BASE}/exports/teams.xlsx`} target="_blank" rel="noreferrer">
              <Download className="w-4 h-4 mr-2" /> Download Teams
            </a>
          </Button>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#F4B400]/60 bg-[#F4B400]/10 text-[#F4B400] text-sm font-bold uppercase tracking-widest cursor-pointer hover:bg-[#F4B400]/20" data-testid="upload-allocations-label">
            <Upload className="w-4 h-4" />
            {busy ? "Uploading…" : "Upload Allocations"}
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              disabled={busy}
              onChange={(e) => uploadAllocations(e.target.files?.[0])}
              data-testid="upload-allocations-input"
            />
          </label>
          <Button
            variant="outline"
            onClick={() => setModal({ kind: "snapshots" })}
            className="border-purple-400/50 text-purple-200 hover:bg-purple-500/10"
            data-testid="btn-snapshot-squad"
          >
            <Camera className="w-4 h-4 mr-2" /> Snapshot Squad
          </Button>
          <Button variant="outline" onClick={reload} className="border-white/20 text-white" data-testid="btn-reload-corrections">
            <RefreshCw className="w-4 h-4 mr-2" /> Reload
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="tc-stats">
        <Chip label="Teams" value={snap.teams.length} testid="tc-stat-teams" />
        <Chip label="Unsold Players" value={snap.unsold_players} testid="tc-stat-unsold" />
        <Chip label="Sold" value={snap.teams.reduce((n, t) => n + t.roster_size, 0)} testid="tc-stat-sold" />
        <Chip label="Total Spent (all teams)" value={fmtKES(snap.teams.reduce((n, t) => n + t.spent, 0))} testid="tc-stat-spent" />
      </div>

      <div className="space-y-4">
        {snap.teams.map(team => (
          <TeamCard
            key={team.team_id}
            team={team}
            onAdd={() => setModal({ kind: "add", team })}
            onRemove={(pl) => removePlayer(team, pl)}
            onMove={(pl) => setModal({ kind: "move", team, player: pl })}
            onReverseSale={(pl) => setModal({ kind: "reverse-sale", team, player: pl })}
            onTxns={() => setModal({ kind: "txns", team })}
            onBidHistory={(pl) => setModal({ kind: "bid-history", team, player: pl })}
            busy={busy}
          />
        ))}
      </div>

      {/* Action modals */}
      {modal?.kind === "add" && (
        <AddPlayerDialog
          team={modal.team}
          freeAgents={freeAgents}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); await reload(); }}
        />
      )}
      {modal?.kind === "move" && (
        <MovePlayerDialog
          team={modal.team}
          player={modal.player}
          teams={snap.teams}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); await reload(); }}
        />
      )}
      {modal?.kind === "reverse-sale" && (
        <ReverseSaleDialog
          team={modal.team}
          player={modal.player}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); await reload(); }}
        />
      )}
      {modal?.kind === "txns" && (
        <TransactionsDialog team={modal.team} onClose={() => setModal(null)} />
      )}
      {modal?.kind === "bid-history" && (
        <BidHistoryDialog player={modal.player} onClose={() => setModal(null)} />
      )}
      {modal?.kind === "snapshots" && (
        <SnapshotsDialog onClose={() => setModal(null)} />
      )}
    </div>
  );
}

function Chip({ label, value, testid }) {
  return (
    <div className="px-4 py-3 rounded-xl bg-[#0d1a13] border border-white/10" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-anton mt-0.5 text-white">{value}</div>
    </div>
  );
}

function TeamCard({ team, onAdd, onRemove, onMove, onReverseSale, onTxns, onBidHistory, busy }) {
  return (
    <Card className="bg-[#0d1a13] border-white/10 overflow-hidden" data-testid={`tc-team-${team.team_id}`}>
      <div className="p-4 border-b border-white/10 flex items-center gap-3 flex-wrap">
        {team.logo_url ? (
          <img src={team.logo_url} alt={team.team_name} className="w-10 h-10 rounded object-contain bg-black/40" />
        ) : (
          <div className="w-10 h-10 rounded bg-black/40 border border-white/10" />
        )}
        <div className="flex-1 min-w-[180px]">
          <div className="font-anton text-xl uppercase text-white">{team.team_name}</div>
          <div className="text-[10px] text-zinc-500">
            Purse: {fmtKES(team.purse_remaining)} of {fmtKES(team.purse_initial)} · Spent: <b className="text-white">{fmtKES(team.spent)}</b> · Roster: {team.roster_size}
          </div>
        </div>
        <Button size="sm" onClick={onAdd} disabled={busy} className="bg-emerald-500 text-black font-bold" data-testid={`btn-tc-add-${team.team_id}`}>
          <Plus className="w-4 h-4 mr-1.5" /> Add
        </Button>
        <Button size="sm" variant="outline" onClick={onTxns} className="border-white/20 text-white" data-testid={`btn-tc-txns-${team.team_id}`}>
          <Wallet className="w-4 h-4 mr-1.5" /> Transactions
        </Button>
      </div>
      {team.roster.length === 0 ? (
        <div className="text-xs text-zinc-500 px-4 py-3 italic">No players yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
              <th className="text-left px-4 py-2">Player</th>
              <th className="text-left px-2 py-2 w-16">Cat</th>
              <th className="text-right px-2 py-2 w-24">Price</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {team.roster.map(p => (
              <tr key={p.id} className="border-b border-white/5" data-testid={`tc-player-${p.id}`}>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.name} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-black/40 border border-white/10" />
                    )}
                    <div>
                      <div className="text-white">{p.name}</div>
                      <div className="text-[10px] text-zinc-500">
                        {typeof p.current_ranking === "number" ? `KE #${p.current_ranking}` : ""}
                        {typeof p.fip_ranking === "number" ? ` · FIP ${p.fip_ranking}` : ""}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2"><Badge className="bg-white/10 text-white/80 border-white/10">{p.category || "?"}</Badge></td>
                <td className="px-2 py-2 text-right font-mono text-white">{fmtKES(p.sold_amount)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => onBidHistory(p)} className="mr-1 h-7 border-white/20 text-white" data-testid={`btn-tc-history-${p.id}`}>
                    <History className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onMove(p)} disabled={busy} className="mr-1 h-7 border-blue-500/40 text-blue-300 hover:bg-blue-500/10" data-testid={`btn-tc-move-${p.id}`}>
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Move
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onReverseSale(p)} disabled={busy} className="mr-1 h-7 border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid={`btn-tc-reverse-${p.id}`}>
                    <Undo2 className="w-3.5 h-3.5 mr-1" /> Reverse Sale
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onRemove(p)} disabled={busy} className="h-7 border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`btn-tc-remove-${p.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ============================================================ */
/* DIALOGS                                                       */
/* ============================================================ */
function AddPlayerDialog({ team, freeAgents, onClose, onDone }) {
  const [pid, setPid] = useState("");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = Number(price);
    if (!pid || Number.isNaN(n) || n < 0) return toast.error("Pick a player and enter a valid price");
    setBusy(true);
    try {
      await api.post(`/admin/teams/${team.team_id}/players/add`, { player_id: pid, price: n });
      toast.success("Player added");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0f1a15] border-white/10 text-white" data-testid="add-player-dialog">
        <DialogHeader>
          <DialogTitle>Add Player to {team.team_name}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Only free-agents (players not currently on any team) are shown. Purse is debited by the price.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1">Free-agent</div>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="add-player-select"><SelectValue placeholder={freeAgents.length ? "Pick a player" : "No free-agents available"} /></SelectTrigger>
              <SelectContent>
                {freeAgents.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name} · {p.category || "?"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1">Price (KES)</div>
            <Input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0" data-testid="add-player-price" />
            <div className="text-[10px] text-zinc-500 mt-1">Team purse remaining: {fmtKES(team.purse_remaining)}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="add-player-cancel">Cancel</Button>
          <Button className="bg-[#F4B400] text-black font-bold" onClick={submit} disabled={busy} data-testid="add-player-confirm">
            {busy ? "Adding…" : "Add + Charge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MovePlayerDialog({ team, player, teams, onClose, onDone }) {
  const [target, setTarget] = useState("");
  const [price, setPrice] = useState(String(player.sold_amount || 0));
  const [busy, setBusy] = useState(false);
  const options = useMemo(() => teams.filter(t => t.team_id !== team.team_id), [teams, team.team_id]);
  const submit = async () => {
    if (!target) return toast.error("Pick a target team");
    const n = Number(price);
    if (Number.isNaN(n) || n < 0) return toast.error("Invalid price");
    if (!window.confirm(`Move ${player.name} from ${team.team_name} → ${options.find(t => t.team_id === target)?.team_name} for ${fmtKES(n)}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/teams/${team.team_id}/players/move`, { player_id: player.id, target_team_id: target, price: n });
      toast.success("Player moved · both purses updated");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0f1a15] border-white/10 text-white" data-testid="move-player-dialog">
        <DialogHeader>
          <DialogTitle>Move {player.name}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Refunds <b className="text-emerald-300">{fmtKES(player.sold_amount)}</b> to {team.team_name} and charges the target team. Price defaults to the previous amount but you can override.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1">Target team</div>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="move-target-select"><SelectValue placeholder="Pick a target team" /></SelectTrigger>
              <SelectContent>
                {options.map(t => (
                  <SelectItem key={t.team_id} value={t.team_id}>{t.team_name} · purse {fmtKES(t.purse_remaining)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1">Price on new team (KES)</div>
            <Input type="number" value={price} onChange={e => setPrice(e.target.value)} data-testid="move-price" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="move-cancel">Cancel</Button>
          <Button className="bg-blue-500 text-white font-bold" onClick={submit} disabled={busy} data-testid="move-confirm">
            {busy ? "Moving…" : "Move Player"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReverseSaleDialog({ team, player, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [saleId, setSaleId] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    // Find the latest non-reversed sale event for this player+team
    (async () => {
      try {
        const { data } = await api.get(`/admin/teams/${team.team_id}/transactions`);
        const sale = (data || []).find(t => t.player_id === player.id && t.action === "auction.sold" && !t._reverse_marker);
        if (!sale) {
          setErr("No open auction sale event found for this pair. Use Remove instead.");
          return;
        }
        setSaleId(sale.ref_id);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [team.team_id, player.id]);
  const submit = async () => {
    if (!saleId) return;
    setBusy(true);
    try {
      await api.post(`/auction/sale/${saleId}/reverse`, {});
      toast.success("Sale reversed · player back in pool");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0f1a15] border-white/10 text-white" data-testid="reverse-sale-dialog">
        <DialogHeader>
          <DialogTitle>Reverse Sale — {player.name}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            The player will return to the auction pool and {fmtKES(player.sold_amount)} will be refunded to {team.team_name}.
            An audit record is kept.
          </DialogDescription>
        </DialogHeader>
        {err && (
          <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2 flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {err}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reverse-sale-cancel">Cancel</Button>
          <Button className="bg-amber-500 text-black font-bold" onClick={submit} disabled={busy || !saleId} data-testid="reverse-sale-confirm">
            {busy ? "Reversing…" : "Reverse Sale"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransactionsDialog({ team, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/admin/teams/${team.team_id}/transactions`).then(({ data }) => setRows(data || []));
  }, [team.team_id]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0f1a15] border-white/10 text-white max-w-2xl" data-testid="txns-dialog">
        <DialogHeader>
          <DialogTitle>Wallet Transactions · {team.team_name}</DialogTitle>
          <DialogDescription className="text-zinc-400">Immutable ledger of every purse mutation on this team.</DialogDescription>
        </DialogHeader>
        {!rows ? <div className="text-zinc-500 text-sm">Loading…</div> : rows.length === 0 ? (
          <div className="text-zinc-500 text-sm">No transactions yet.</div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <th className="text-left px-2 py-2">When</th>
                  <th className="text-left px-2 py-2">Action</th>
                  <th className="text-right px-2 py-2">Amount</th>
                  <th className="text-left px-2 py-2">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b border-white/5" data-testid={`txn-${r.id}`}>
                    <td className="px-2 py-1 text-[11px] text-zinc-400">{r.created_at?.replace("T", " ").slice(0, 16)}</td>
                    <td className="px-2 py-1 text-white">{r.action}</td>
                    <td className={`px-2 py-1 text-right font-mono ${Number(r.amount) < 0 ? "text-red-300" : "text-emerald-300"}`}>
                      {Number(r.amount) < 0 ? "" : "+"}{fmtKES(r.amount)}
                    </td>
                    <td className="px-2 py-1 text-[11px] text-zinc-400">{r.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="txns-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SnapshotsDialog({ onClose }) {
  const [rows, setRows] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/squad-snapshots"); setRows(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/squad-snapshots", { note });
      toast.success(`Snapshot taken · ${data.team_count} teams · ${data.total_players} players`);
      setNote("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const openSnap = async (id) => {
    try { const { data } = await api.get(`/admin/squad-snapshots/${id}`); setViewing(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0f1a15] border-white/10 text-white max-w-3xl" data-testid="snapshots-dialog">
        <DialogHeader>
          <DialogTitle>Squad Snapshots</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Point-in-time freeze of every team's roster + purse. Take one BEFORE any bulk xlsx upload — you can always look back to see the pre-change state.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1">Note (optional)</div>
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder='e.g. "Pre auction-day-3 upload"' data-testid="snapshot-note" />
          </div>
          <Button className="bg-purple-500 hover:bg-purple-400 text-white font-bold" onClick={create} disabled={busy} data-testid="snapshot-take">
            <Camera className="w-4 h-4 mr-2" /> {busy ? "Freezing…" : "Take Snapshot"}
          </Button>
        </div>
        <div className="max-h-[380px] overflow-y-auto mt-2">
          {!rows ? <div className="text-zinc-500 text-sm">Loading…</div> : rows.length === 0 ? (
            <div className="text-zinc-500 text-sm italic">No snapshots yet. Take one to bookmark the current state.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <th className="text-left px-2 py-2">When</th>
                  <th className="text-left px-2 py-2">By</th>
                  <th className="text-left px-2 py-2">Teams</th>
                  <th className="text-left px-2 py-2">Players</th>
                  <th className="text-left px-2 py-2">Note</th>
                  <th className="text-right px-2 py-2">Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b border-white/5" data-testid={`snapshot-${r.id}`}>
                    <td className="px-2 py-1 text-[11px] text-zinc-400">{r.created_at?.replace("T", " ").slice(0, 16)}</td>
                    <td className="px-2 py-1 text-zinc-300">{r.created_by_name || r.created_by?.slice(0, 8)}</td>
                    <td className="px-2 py-1 text-white">{r.team_count}</td>
                    <td className="px-2 py-1 text-white">{r.total_players}</td>
                    <td className="px-2 py-1 text-zinc-400 max-w-[240px] truncate" title={r.note}>{r.note || "—"}</td>
                    <td className="px-2 py-1 text-right">
                      <Button size="sm" variant="outline" className="h-7 border-white/20 text-white" onClick={() => openSnap(r.id)} data-testid={`snapshot-open-${r.id}`}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {viewing && (
          <div className="mt-4 pt-3 border-t border-white/10">
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2">
              Snapshot · {viewing.created_at?.replace("T", " ").slice(0, 16)} · {viewing.note || "no note"}
            </div>
            <div className="max-h-[220px] overflow-y-auto grid sm:grid-cols-2 gap-2">
              {viewing.frames.map(f => (
                <div key={f.team_id} className="bg-black/30 rounded p-2 text-xs" data-testid={`snapshot-frame-${f.team_id}`}>
                  <div className="text-white font-bold">{f.team_name}</div>
                  <div className="text-[10px] text-zinc-500">Purse: {fmtKES(f.purse_remaining)} · Spent: {fmtKES(f.purse_initial - f.purse_remaining)} · {f.roster.length} players</div>
                  <ul className="mt-1 text-[10px] text-zinc-300">
                    {f.roster.slice(0, 6).map(p => <li key={p.id}>• {p.name} · {p.category || "?"} · {fmtKES(p.sold_amount)}</li>)}
                    {f.roster.length > 6 && <li className="text-zinc-500">…and {f.roster.length - 6} more</li>}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="snapshot-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BidHistoryDialog({ player, onClose }) {  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/admin/players/${player.id}/bid-history`).then(({ data }) => setRows(data || []));
  }, [player.id]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0f1a15] border-white/10 text-white max-w-2xl" data-testid="bid-history-dialog">
        <DialogHeader>
          <DialogTitle>Bid History · {player.name}</DialogTitle>
          <DialogDescription className="text-zinc-400">Every bid placed on this player, including reversed ones.</DialogDescription>
        </DialogHeader>
        {!rows ? <div className="text-zinc-500 text-sm">Loading…</div> : rows.length === 0 ? (
          <div className="text-zinc-500 text-sm">No bid history recorded.</div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <th className="text-left px-2 py-2">When</th>
                  <th className="text-left px-2 py-2">Team</th>
                  <th className="text-right px-2 py-2">Amount</th>
                  <th className="text-right px-2 py-2">Prev</th>
                  <th className="text-left px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b border-white/5" data-testid={`bid-${r.id}`}>
                    <td className="px-2 py-1 text-[11px] text-zinc-400">{r.ts?.replace("T", " ").slice(0, 16)}</td>
                    <td className="px-2 py-1 text-white">{r.team_name || r.team_id?.slice(0, 8)}</td>
                    <td className="px-2 py-1 text-right font-mono text-white">{fmtKES(r.amount)}</td>
                    <td className="px-2 py-1 text-right font-mono text-zinc-400">{fmtKES(r.prev_amount || 0)}</td>
                    <td className="px-2 py-1">
                      <Badge className={r.status === "reversed" ? "bg-red-500/20 text-red-300 border-red-500/30" : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"}>
                        {r.status || "active"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bid-history-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
